// Port of ios/Sources/ViewModels/ChannelListDisplayModels.swift.

/** Why a conversation is read-only. */
export type ChannelInactiveReason =
  /** The channel was archived (shared/API.md §26). */
  | 'archived'
  /** A direct message whose only other participant is a deactivated account (§2.4). */
  | 'deactivatedPartner';

/**
 * One person inside a group DM's avatar cluster — everything the renderer needs to draw a face
 * and nothing else. `lastPictureUpdate` is the avatar's cache version; `displayName` only ever
 * feeds the deterministic initials placeholder behind a photo that has not loaded.
 * Mirrors Kotlin's `GroupFace`. (Swift's `id` is `userId`.)
 */
export interface GroupFace {
  userId: string;
  displayName: string;
  lastPictureUpdate: number;
}

/**
 * One row in the channel list, already resolved for display — the View never looks at a raw
 * `ChannelRecord`. `dmPartnerName`/`dmPartnerUsername` are carried along only so local search
 * can match against them (docs/CHANNEL-LIST.md); they're `null` for "O"/"P"/"G" rows.
 *
 * The optional fields are Swift's defaulted `var`s; `displayChannel()` fills them.
 */
export interface DisplayChannel {
  id: string;
  type: string;
  name: string;
  isUnread: boolean;
  mentionCount: number;
  isMuted: boolean;
  lastPostAt: number;
  dmPartnerName: string | null;
  dmPartnerUsername: string | null;
  /**
   * The DM partner's id/avatar version/presence (docs/VISUAL-DESIGN.md §5's "DM/GM the 32
   * avatar with presence dot") — `null` for "O"/"P"/"G" rows. Group rows carry `groupFaces`
   * instead: they are several people, so they get several faces.
   */
  dmPartnerId: string | null;
  dmPartnerLastPictureUpdate: number;
  dmPartnerPresence: string | null;
  /**
   * The DM partner's CUSTOM status emoji, as the character to draw — what they SAY about
   * themselves, beside the dot that says whether they are connected ("on the chat list show
   * their status beside their connection status", user 2026-09-12).
   *
   * Already resolved and already expiry-checked by the view model: a status whose `expires_at`
   * has passed is not a status, and the server leaves expired ones sitting in `props` (probed
   * live 2026-09-12 — real accounts still carrying "Out for lunch" from 2025). null when there
   * is none, when it has expired, or when its emoji name is not in this build's table; in all
   * three the row draws nothing rather than a placeholder.
   */
  dmPartnerCustomStatusEmoji: string | null;
  /** That status's sentence, for the accessibility label and the desktop's tooltip. */
  dmPartnerCustomStatusText: string;
  /**
   * The faces a group DM draws in its leading slot, in the same order the title lists their
   * names (`groupFaceIds`). Empty for every other channel type, and empty for a group whose
   * participants have not synced yet — the row falls back to a silhouette rather than
   * inventing anyone.
   */
  groupFaces: GroupFace[];
  /**
   * The second line: draft > last message > "Sin mensajes" (only when lastPostAt == 0) >
   * null, which renders as a BLANK line — unsynced history must never claim emptiness.
   */
  preview: string | null;
  /**
   * True when `preview` is showing a reply written INSIDE A THREAD rather than a message posted
   * in the conversation itself. The row draws a leading ↳ so the two are never confused: under
   * collapsed threads a reply is the channel's newest post but is NOT part of the channel — open
   * the conversation looking for it and it is not there, it is one level down (user-reported).
   */
  previewIsThreadReply: boolean;
  /**
   * When `preview`'s message was posted. 0 for a draft line, for "Sin mensajes" and for a row
   * with nothing cached — the row's own trailing stamp still reads `lastPostAt`, which is the
   * channel's activity rather than this particular message's.
   */
  previewAt: number;
  /**
   * The id of the post `preview` is showing, or empty for a draft line or an empty row. The
   * desktop jumps TO it (its jump path resolves a reply's root and opens the thread pane on
   * the reply itself), which is why the id and not just the thread's root is carried.
   */
  previewPostId: string;
  /**
   * The thread that preview belongs to, so a tap can offer to open it instead of the channel.
   *
   * The ↳ glyph tells the reader the newest message lives one level down; it does not get them
   * there. Without this the row could only ever open the channel, where the message they just
   * read in the list is not visible — the same dead end the glyph was added to warn about.
   * null whenever `previewIsThreadReply` is false.
   */
  previewThreadRootId: string | null;
  /**
   * The conversation's own newest message — the last thing the reader would SEE on opening the
   * channel, which under collapsed threads is not `preview` at all. Same shape as `preview`:
   * sender-prefixed, flattened, Markdown-stripped.
   *
   * Populated ONLY for rows previewing a thread reply (`previewThreadRootId != null`), because
   * it costs a second grouped query and nothing else in the app asks the question. Empty for
   * every other row, and also for a thread-reply row in a channel whose only root posts have
   * aged out of the local cache — a real state, rendered as "no messages" rather than blank.
   *
   * Exists so the thread-or-channel chooser can show what is behind each door instead of
   * labelling them: the two destinations are distinguishable by their CONTENT, not by the
   * words "thread" and "conversation", which describe the mechanism rather than what is there.
   */
  channelPreview: string;
  /** When `channelPreview` was posted. 0 when there is no such message. */
  channelPreviewAt: number;
  /**
   * How many replies in this conversation's followed threads the reader has not read, summed
   * across those threads — server truth from the `threads` table, not the local post cache.
   *
   * Deliberately NOT folded into `isUnread`: under `CollapsedThreads=always_on` a reply does not
   * make the channel unread, and diverging from the webapp there was ruled out (2026-08-28). It
   * is a THIRD, quieter state, drawn as a thread-tinted marker rather than the unread dot.
   */
  unreadThreadReplies: number;
  /**
   * Why this conversation is read-only, when it is (Android's InactiveReason, verbatim):
   * hidden from the normal list, still findable by SEARCHING — someone looking for an old
   * archived channel by name must find it; nobody should trip over it otherwise.
   */
  inactive: ChannelInactiveReason | null;
}

/** The fields Swift's memberwise init requires; everything else takes Swift's default. */
export type DisplayChannelRequired = Pick<
  DisplayChannel,
  | 'id'
  | 'type'
  | 'name'
  | 'isUnread'
  | 'mentionCount'
  | 'isMuted'
  | 'lastPostAt'
  | 'dmPartnerName'
  | 'dmPartnerUsername'
  | 'dmPartnerId'
  | 'dmPartnerLastPictureUpdate'
  | 'dmPartnerPresence'
>;

/** Swift's memberwise init: the required fields plus any of the defaulted ones. */
export function displayChannel(fields: DisplayChannelRequired & Partial<DisplayChannel>): DisplayChannel {
  return {
    dmPartnerCustomStatusEmoji: null,
    dmPartnerCustomStatusText: '',
    groupFaces: [],
    preview: null,
    previewIsThreadReply: false,
    previewAt: 0,
    previewPostId: '',
    previewThreadRootId: null,
    channelPreview: '',
    channelPreviewAt: 0,
    unreadThreadReplies: 0,
    inactive: null,
    ...fields,
  };
}

/** One sidebar category with its already-ordered, already-resolved rows. */
export interface ChannelListSection {
  id: string;
  displayName: string;
  type: string;
  collapsed: boolean;
  /**
   * The whole category is muted (custom categories only). The header dims and wears the bell —
   * the one affordance that explains why every row beneath it is dimmed.
   */
  isMuted: boolean;
  rows: DisplayChannel[];
}

/** Swift's memberwise init (`isMuted` defaults to false). */
export function channelListSection(
  fields: Omit<ChannelListSection, 'isMuted'> & { isMuted?: boolean },
): ChannelListSection {
  return { ...fields, isMuted: fields.isMuted ?? false };
}

/**
 * A collapsed category still shows its aggregate indicator (docs/CHANNEL-LIST.md); muted
 * channels never contribute to it. (Swift's computed `hasUnread`.)
 */
export function sectionHasUnread(section: ChannelListSection): boolean {
  return section.rows.some((r) => r.isUnread && !r.isMuted);
}

/** Swift's computed `mentionCount`: unmuted mentions summed. */
export function sectionMentionCount(section: ChannelListSection): number {
  return section.rows.reduce((sum, r) => sum + (r.isMuted ? 0 : r.mentionCount), 0);
}
